/**
 * VITAL — Toast & ATC Micro-Interaction System
 *
 * Micro-interaction specs:
 *
 * [ATC Button]
 *   Trigger:   click/tap on [data-vital-atc]
 *   States:    idle → pressing(80ms) → loading → success(2000ms) → idle
 *              idle → pressing → loading → error(1500ms) → idle
 *   Feedback:  sweep light, spinner, drawn checkmark, shake + red flash
 *   Loops:     single-shot, resets automatically
 *   A11y:      aria-label updates on state, aria-live region
 *
 * [Toast]
 *   Trigger:   VitalToast.show({ ... }) called from ATC success
 *   Rules:     max 3 concurrent, stack bottom-right, auto-dismiss 3.5s
 *              hover pauses timer, close button dismisses immediately
 *   Feedback:  slide-up + scale-in, amber glow pulse, lima checkmark,
 *              progress bar drains, slide-out on dismiss
 *   Loops:     none (single-shot per cart add)
 *   A11y:      role="status", aria-live="polite"
 */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var MAX_TOASTS = 3;

  /* ═══════════════════════════════════════════════
     TOAST ENGINE
  ════════════════════════════════════════════════ */
  var VitalToast = (function () {
    var root = null;
    var liveRegion = null;
    var toasts = [];

    function getRoot() {
      if (!root) {
        root = document.createElement('div');
        root.id = 'vital-toast-root';
        root.setAttribute('aria-live', 'off'); /* announcements via separate region */
        document.body.appendChild(root);
      }
      return root;
    }

    function getLiveRegion() {
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        Object.assign(liveRegion.style, {
          position: 'absolute', left: '-9999px', top: '-9999px',
          width: '1px', height: '1px', overflow: 'hidden'
        });
        document.body.appendChild(liveRegion);
      }
      return liveRegion;
    }

    function dismiss(toast) {
      if (toast._dismissed) return;
      toast._dismissed = true;
      clearTimeout(toast._timer);
      cancelAnimationFrame(toast._raf);

      var el = toast.el;
      el.classList.add('v-toast--out');

      setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
        toasts = toasts.filter(function (t) { return t !== toast; });
      }, prefersReduced ? 0 : 240);
    }

    function show(opts) {
      /*
       * opts: {
       *   title:       string  — product name
       *   subtitle:    string  — e.g. "1 × Foco Pro 60 Caps"
       *   image:       string  — product image URL (optional)
       *   cartUrl:     string  — "/cart"
       *   duration:    number  — ms, default 3500
       * }
       */
      if (toasts.length >= MAX_TOASTS) {
        dismiss(toasts[toasts.length - 1]);
      }

      var duration = opts.duration || 3500;
      var r = getRoot();

      /* ── Build DOM ── */
      var el = document.createElement('div');
      el.className = 'v-toast';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-atomic', 'true');

      /* Thumb */
      var thumbHTML = opts.image
        ? '<div class="v-toast__thumb"><img src="' + opts.image + '" alt="" loading="lazy"></div>'
        : '<div class="v-toast__thumb"><span class="v-toast__thumb-icon">💊</span></div>';

      /* Body */
      el.innerHTML = thumbHTML +
        '<div class="v-toast__body">' +
          '<div class="v-toast__label">' +
            '<svg viewBox="0 0 16 16" aria-hidden="true"><polyline points="2.5 8 6.5 12 13.5 4"/></svg>' +
            'Agregado al carrito' +
          '</div>' +
          '<div class="v-toast__title">' + _esc(opts.title || 'Producto') + '</div>' +
          (opts.subtitle ? '<div class="v-toast__sub">' + _esc(opts.subtitle) + '</div>' : '') +
          '<button type="button" class="v-toast__cta" onclick="if(window.VitalCart){window.VitalCart.open();}else{window.location.href=\'' + (opts.cartUrl || '/cart') + '\';}">' +
            'Ver carrito' +
            '<svg viewBox="0 0 16 16" aria-hidden="true"><polyline points="6 3 11 8 6 13"/></svg>' +
          '</button>' +
        '</div>' +
        '<button class="v-toast__close" aria-label="Cerrar notificación" type="button">' +
          '<svg viewBox="0 0 16 16" aria-hidden="true"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>' +
        '</button>' +
        '<div class="v-toast__progress"><div class="v-toast__progress-fill"></div></div>';

      r.insertBefore(el, r.firstChild);

      var toast = { el: el, _dismissed: false, _timer: null, _raf: null };
      toasts.unshift(toast);

      /* Close button */
      el.querySelector('.v-toast__close').addEventListener('click', function () {
        dismiss(toast);
      });

      /* Hover pause */
      var remaining = duration;
      var startTime = null;

      function startTimer() {
        startTime = Date.now();
        toast._timer = setTimeout(function () { dismiss(toast); }, remaining);
      }

      function pauseTimer() {
        clearTimeout(toast._timer);
        remaining -= (Date.now() - startTime);
      }

      el.addEventListener('mouseenter', pauseTimer);
      el.addEventListener('mouseleave', startTimer);

      /* Progress bar */
      var fill = el.querySelector('.v-toast__progress-fill');
      if (fill && !prefersReduced) {
        fill.style.width = '100%';
        fill.style.transition = 'width ' + duration + 'ms linear';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            fill.style.width = '0%';
          });
        });
      }

      /* Announce to screen readers */
      getLiveRegion().textContent = 'Agregado al carrito: ' + (opts.title || 'Producto');

      startTimer();
      return toast;
    }

    return { show: show, dismiss: dismiss };
  })();

  /* ═══════════════════════════════════════════════
     ATC BUTTON ENHANCEMENT
     Applies to any [data-vital-atc] button/anchor
     Replaces the inline vitalAddToCart fn if present
  ════════════════════════════════════════════════ */
  function setState(btn, state, content) {
    btn.setAttribute('data-state', state);
    if (content !== undefined) btn.innerHTML = content;
  }

  function spinnerHTML(label) {
    return '<span class="v-atc-spinner" aria-hidden="true">' +
      '<span class="v-atc-spinner__ring"></span>' +
    '</span>' +
    '<span class="v-sr-only">' + label + '</span>';
  }

  function checkHTML(label) {
    return '<span class="v-atc-check">' +
      '<svg class="v-atc-check__svg" viewBox="0 0 16 16" aria-hidden="true">' +
        '<path class="v-atc-check__path" d="M2.5 8.5L6.5 12.5L13.5 4"/>' +
      '</svg>' +
      label +
    '</span>';
  }

  function addToCart(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId || btn.getAttribute('data-state') === 'loading') return;

    var originalHTML = btn.innerHTML;
    var originalLabel = btn.getAttribute('aria-label') || 'Agregar al carrito';

    /* pressing */
    setState(btn, 'pressing');

    /* loading after press feedback */
    setTimeout(function () {
      setState(btn, 'loading', spinnerHTML('Agregando…'));
      btn.setAttribute('aria-label', 'Agregando al carrito…');
      btn.disabled = true;
    }, prefersReduced ? 0 : 80);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('cart_error');
      return r.json();
    })
    .then(function (lineItem) {
      /* success state */
      setState(btn, 'success', checkHTML('¡Listo!'));
      btn.setAttribute('aria-label', '¡Agregado al carrito!');

      /* Update cart badge */
      return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        document.querySelectorAll('.vital-cart-badge, .vital-d-cart-badge, [data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.style.display = 'flex';
        });

        /* Fire toast */
        var imgUrl = (lineItem.featured_image && lineItem.featured_image.url)
          ? lineItem.featured_image.url.replace(/\?.*$/, '') + '?width=120'
          : null;

        VitalToast.show({
          title: lineItem.title || lineItem.product_title || 'Producto',
          subtitle: (lineItem.quantity || 1) + ' × ' + (lineItem.variant_title && lineItem.variant_title !== 'Default Title' ? lineItem.variant_title : ''),
          image: imgUrl,
          cartUrl: '/cart',
          duration: 3500
        });
      });
    })
    .catch(function () {
      setState(btn, 'error', '¡Error! Intenta de nuevo');
      btn.setAttribute('aria-label', 'Error al agregar al carrito');
    })
    .finally(function () {
      /* reset after delay */
      var delay = btn.getAttribute('data-state') === 'error' ? 1800 : 2000;
      setTimeout(function () {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
        btn.setAttribute('aria-label', originalLabel);
        setState(btn, 'idle');
        btn.removeAttribute('data-state');
      }, delay);
    });
  }

  /* ── Bind all ATC buttons — now and future (event delegation) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-vital-atc]');
    if (!btn) return;
    e.preventDefault();
    addToCart(btn);
  });

  /* ── Also patch legacy vitalAddToCart for backward compat ── */
  window.vitalAddToCart = function (btn, variantId) {
    if (!btn.hasAttribute('data-vital-atc')) {
      btn.setAttribute('data-vital-atc', '');
    }
    btn.setAttribute('data-variant-id', variantId);
    addToCart(btn);
  };

  /* ── Helper ── */
  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  window.VitalToast = VitalToast;
  window.VitalAddToCart = addToCart;

})();

/* Screen-reader only utility */
(function () {
  if (document.getElementById('v-sr-style')) return;
  var s = document.createElement('style');
  s.id = 'v-sr-style';
  s.textContent = '.v-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}';
  document.head.appendChild(s);
})();
